import type { ProjectType } from '@atlassian/jira-common-constants';
import { ff } from '@atlassian/jira-feature-flagging';
import type { ImportType } from '../../../constants/import-type';
import { ProjectTypes } from '../../../constants/project';
import { EXISTING_PROJECT, type Source } from '../../../constants/source';
import {
	type StepComponent,
	DEFAULT_CONFIG,
	MAPPING_STEP,
	UPLOAD_CSV_STANDARD_STEP,
	IMPORT_IN_PROGRESS_STEP,
	PROJECT_CREATE_STEP,
	UPLOAD_CSV_STEP,
	templatesJSW,
	SELECT_SOURCE_STEP,
	CSV_USER_MAPPING_STEP,
	REVIEW_SCREEN_STANDARD_STEP,
	CSVReviewTilesType,
} from '../../../constants/steps';
import messages from '../messages';

export const getCSVSteps = (projectType: ProjectType, source?: Source): StepComponent[] => {
	if (source === EXISTING_PROJECT) {
		return [
			{
				...DEFAULT_CONFIG,
				step: MAPPING_STEP,
				overrides: { overrideReturnStep: UPLOAD_CSV_STANDARD_STEP },
				stepProps: {
					shouldStartImportOnNext: true,
					showLearnMoreLink: projectType === ProjectTypes.BUSINESS,
					isCompact: true,
					hideFeedback: true,
					rightAlignFooter: true,
					offsetModalY: 60,
				},
			},
			{
				step: IMPORT_IN_PROGRESS_STEP,
				overrides: { overrideReturnStep: UPLOAD_CSV_STANDARD_STEP },
				stepProps: {
					hasProgressBar: false,
					showCrossButton: true,
					isCompact: true,
				},
			},
		];
	}
	return [
		{
			...DEFAULT_CONFIG,
			step: PROJECT_CREATE_STEP,
			overrides: { overrideReturnStep: UPLOAD_CSV_STEP },
			stepProps: {
				hasProgressBar: false,
				shouldPrefillThirdPartyProjectName: false,
				shouldShowThirdPartyStats: false,
				shouldStartImportOnNext: false,
				isTMPSupported: true,
				templates: projectType === ProjectTypes.SOFTWARE ? templatesJSW : [],
			},
			progressBarStepLabel: messages.projectCreateStepLabel,
		},
		{
			...DEFAULT_CONFIG,
			step: MAPPING_STEP,
			stepProps: {
				shouldStartImportOnNext: true,
				showLearnMoreLink: projectType === ProjectTypes.BUSINESS,
			},
		},
		{
			step: IMPORT_IN_PROGRESS_STEP,
			overrides: { overrideReturnStep: UPLOAD_CSV_STEP },
			stepProps: {
				hasProgressBar: false,
			},
		},
	];
};

export const getStandardisedCSVSteps = ({
	importType,
	source,
	isSiteAdmin,
	isAdmin,
}: {
	importType: ImportType;
	source?: Source;
	isSiteAdmin?: boolean;
	isAdmin?: boolean;
}): StepComponent[] => {
	if (source === EXISTING_PROJECT) {
		return [
			{
				...DEFAULT_CONFIG,
				step: UPLOAD_CSV_STANDARD_STEP,
				overrides: { overrideReturnStep: SELECT_SOURCE_STEP },
				stepProps: {
					isCompact: true,
					importType,
				},
			},
			{
				...DEFAULT_CONFIG,
				step: MAPPING_STEP,
				overrides: { overrideReturnStep: UPLOAD_CSV_STANDARD_STEP },
				stepProps: {
					shouldStartImportOnNext: false,
					isCompact: true,
				},
			},
			{
				step: IMPORT_IN_PROGRESS_STEP,
				overrides: { overrideReturnStep: SELECT_SOURCE_STEP },
				stepProps: {
					hasProgressBar: false,
					showCrossButton: true,
					isCompact: true,
				},
			},
		];
	}
	let steps: StepComponent[] = [
		{
			...DEFAULT_CONFIG,
			step: UPLOAD_CSV_STANDARD_STEP,
			overrides: { overrideReturnStep: SELECT_SOURCE_STEP },
			stepProps: {
				isCompact: false,
				importType,
			},
			progressBarStepLabel: messages.uploadCsv,
		},
		{
			...DEFAULT_CONFIG,
			step: PROJECT_CREATE_STEP,
			overrides: { overrideReturnStep: UPLOAD_CSV_STANDARD_STEP },
			stepProps: {
				// TODO: remove hasProgressBar and isTMPSupported when clean up FF 'enable-x2c-standardised-flow'
				hasProgressBar: true,
				isTMPSupported: true,
				shouldPrefillThirdPartyProjectName: false,
				shouldShowThirdPartyStats: false,
				shouldStartImportOnNext: false,
				templates: templatesJSW,
				importType,
			},
			progressBarStepLabel: messages.setupProject,
		},
		{
			...DEFAULT_CONFIG,
			step: MAPPING_STEP,
			stepProps: {
				shouldStartImportOnNext: false,
				showLearnMoreLink: false,
			},
			progressBarStepLabel: messages.mapFields,
		},
		{
			...DEFAULT_CONFIG,
			step: CSV_USER_MAPPING_STEP,
			stepProps: {
				importType,
			},
			progressBarStepLabel: messages.usersMapping,
		},
		{
			...DEFAULT_CONFIG,
			step: REVIEW_SCREEN_STANDARD_STEP,
			stepProps: {
				tiles:
					isSiteAdmin || (isAdmin && ff('enable-product-admin-for-x2c-migration_zmlsv'))
						? [
								CSVReviewTilesType.PROJECT_DETAILS,
								CSVReviewTilesType.FIELDS,
								CSVReviewTilesType.USERS,
								CSVReviewTilesType.ISSUES,
							]
						: [CSVReviewTilesType.PROJECT_DETAILS, CSVReviewTilesType.FIELDS],
				shouldStartImportOnNext: true,
			},
			progressBarStepLabel: messages.review,
		},
		{
			step: IMPORT_IN_PROGRESS_STEP,
			overrides: { overrideReturnStep: SELECT_SOURCE_STEP },
			stepProps: {
				hasProgressBar: false,
			},
		},
	];

	if (!isSiteAdmin && (!isAdmin || !ff('enable-product-admin-for-x2c-migration_zmlsv')))
		steps = steps.filter((step) => step.step !== CSV_USER_MAPPING_STEP);

	return steps;
};
